import { createApi } from '@reduxjs/toolkit/query/react'
import type { Location, CreateLocationRequest, UpdateLocationRequest } from '../../types/location'
import { axiosBaseQuery } from '../../services/baseQuery'

interface UpdateLocationQueryObj {
  locationId: string
  location: UpdateLocationRequest
}

interface AssignPrescribersQueryObj {
  locationId: string
  prescriberIds: string[]
}

export const locationsApi = createApi({
  reducerPath: 'locationsApi',
  tagTypes: ['Locations'],
  baseQuery: axiosBaseQuery({ baseUrl: '/bff/locations' }),
  endpoints: (build) => ({
    getLocations: build.query<Location[], void>({
      query: () => ({
        url: '',
        method: 'get',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Locations' as const, id })),
              { type: 'Locations', id: 'LIST' },
            ]
          : [{ type: 'Locations', id: 'LIST' }],
    }),
    createLocation: build.mutation<Location, CreateLocationRequest>({
      query: (newLocation) => ({
        url: '',
        method: 'post',
        data: newLocation,
      }),
      invalidatesTags: (_result, error) => (error ? [] : [{ type: 'Locations', id: 'LIST' }]),
    }),
    updateLocation: build.mutation<Location, UpdateLocationQueryObj>({
      query: ({ locationId, location }) => ({
        url: `/${locationId}`,
        method: 'patch',
        data: location,
      }),
      invalidatesTags: (_result, error, { locationId }) =>
        error
          ? []
          : [
              { type: 'Locations', id: locationId },
              { type: 'Locations', id: 'LIST' },
            ],
    }),
    assignPrescribers: build.mutation<Location, AssignPrescribersQueryObj>({
      query: ({ locationId, prescriberIds }) => ({
        url: `/${locationId}/prescribers`,
        method: 'post',
        data: { prescriberIds },
      }),
      invalidatesTags: (_result, error, { locationId }) => {
        const status = (error as { status?: number } | undefined)?.status
        if (error && status !== 409) {
          return []
        }
        return [
          { type: 'Locations', id: locationId },
          { type: 'Locations', id: 'LIST' },
        ]
      },
    }),
    removePrescriber: build.mutation<Location, { locationId: string; prescriberId: string }>({
      query: ({ locationId, prescriberId }) => ({
        url: `/${locationId}/prescribers/${prescriberId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, error, { locationId }) =>
        error
          ? []
          : [
              { type: 'Locations', id: locationId },
              { type: 'Locations', id: 'LIST' },
            ],
    }),
  }),
})

export const {
  useGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useAssignPrescribersMutation,
  useRemovePrescriberMutation,
} = locationsApi
